/**
 * Enlace de React con la conexion de la pagina.
 *
 * El hook no abre ni cierra sockets: se **suscribe** a {@link gameConnection},
 * que vive fuera de React. Por eso el doble montaje de `StrictMode` es
 * inofensivo: `ensureConnected` es idempotente y el segundo montaje no abre una
 * segunda conexion.
 *
 * El cliente **no** mantiene una copia autoritativa de la partida: la fuente de
 * verdad visible es el ultimo `snapshot` recibido. Aqui no hay reducer de juego,
 * ni se aplica ninguna accion localmente antes de que el servidor responda.
 */

import { useEffect, useSyncExternalStore } from 'react';

import { gameConnection, type GameRoomState } from './connection';
import type { PlayerAction } from '@tcg/game-rules';

export { GAME_ROOM_NAME, type ConnectionPhase, type GameRoomState } from './connection';

export function useGameRoom(): {
  state: GameRoomState;
  send: (action: PlayerAction) => void;
  retry: () => void;
} {
  const state = useSyncExternalStore(
    gameConnection.subscribe,
    gameConnection.getState,
    gameConnection.getState,
  );

  useEffect(() => {
    void gameConnection.ensureConnected();

    // A proposito **no** se desconecta al desmontar. La conexion pertenece a la
    // pagina y muere con ella; cerrarla aqui volveria a meter el ciclo de vida
    // del socket dentro de React, que es justo lo que causaba el problema.
  }, []);

  return {
    state,
    send: gameConnection.send,
    retry: () => {
      void gameConnection.retry();
    },
  };
}
